import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaYoutube,
  FaFacebookF,
  FaInstagram,
  FaXTwitter,
} from "react-icons/fa6";

// Hybrid header: a painterly backdrop wash (Gemini-generated, no text/logos)
// plus HTML text and four corner logos rendered live by the browser. Sharp
// at any zoom, ~7× lighter than the all-in-one PNG approach, accessible,
// localizable, and cheap to re-theme.

const THEMES = {
  // Each theme picks the matching backdrop wash and a text/accent palette
  // tuned to read cleanly against it.
  // logoShadow: drop-shadow for the transparent Kathakala wordmark — needs
  // to read well against each backdrop. On cream we use a black soft
  // shadow; on dark themes we use a warm cream glow so the silver plate
  // separates from the indigo / maroon backdrop instead of blending in.
  cream: {
    backdrop: "/banners/backdrop-cream.png",
    teluguHero: "text-[#5a3416]",
    english: "text-[#7a4a23]",
    live: "text-[#7a1e2b]",
    livePill: "bg-[#7a1e2b] text-[#f5e6c8] border border-[#7a1e2b]",
    rule: "via-[#c9a04a]/70",
    logoShadow: "drop-shadow-[0_4px_14px_rgba(0,0,0,0.45)]",
    logoGlowShadow: "drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]",
  },
  saraswati: {
    backdrop: "/banners/backdrop-saraswati.png",
    teluguHero: "text-[#f6e3a8] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]",
    english: "text-[#e8d5a0] drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]",
    live: "text-[#fff5d6]",
    livePill:
      "bg-[#c9a04a]/35 text-[#fff5d6] border border-[#c9a04a]/60 backdrop-blur-sm",
    rule: "via-kk-gold/70",
    logoShadow:
      "drop-shadow-[0_0_22px_rgba(245,230,200,0.45)] drop-shadow-[0_4px_14px_rgba(0,0,0,0.5)]",
    logoGlowShadow:
      "drop-shadow-[0_0_24px_rgba(245,230,200,0.35)] drop-shadow-[0_4px_18px_rgba(0,0,0,0.6)]",
  },
  festive: {
    backdrop: "/banners/backdrop-festive.png",
    teluguHero: "text-[#f5e6c8] drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]",
    english: "text-[#f1d894] drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]",
    live: "text-[#fff5d6]",
    livePill:
      "bg-[#c9a04a]/40 text-[#fff5d6] border border-[#c9a84c]/70 backdrop-blur-sm",
    rule: "via-kk-gold/70",
    logoShadow:
      "drop-shadow-[0_0_22px_rgba(245,220,160,0.45)] drop-shadow-[0_4px_14px_rgba(0,0,0,0.5)]",
    logoGlowShadow:
      "drop-shadow-[0_0_24px_rgba(245,220,160,0.35)] drop-shadow-[0_4px_18px_rgba(0,0,0,0.6)]",
  },
};

const ALL_THEME_IDS = ["cream", "saraswati", "festive"];

// Some routes already render their own poster-style hero (Sessions / Utsavam
// embed the festival poster). Suppress the global SiteHeader on those.
function shouldSuppress(pathname) {
  if (!pathname) return false;
  return (
    pathname === "/sessions" ||
    pathname.startsWith("/sessions/") ||
    pathname.startsWith("/utsavam")
  );
}

function pickThemeId(pathname) {
  if (!pathname || pathname === "/") return "cream";
  if (pathname.startsWith("/jeedigunta")) return "saraswati";
  if (pathname.startsWith("/episodes") || pathname.startsWith("/episode-creator")) {
    return "festive";
  }
  // Stable hash for everything else (manage / register / judge / ...).
  let h = 0;
  for (let i = 0; i < pathname.length; i += 1) {
    h = (h * 31 + pathname.charCodeAt(i)) | 0;
  }
  return ALL_THEME_IDS[Math.abs(h) % ALL_THEME_IDS.length];
}

// Compute the next "Saturday 8 PM Central Time" expressed in IST. Result
// shifts by an hour depending on whether Chicago is in CDT (UTC-5) or CST
// (UTC-6) — IST itself doesn't observe DST, so the conversion just follows
// Chicago's offset.
//   • 8 PM Sat CDT → 01:00 UTC Sun → 06:30 IST Sun (6:30 AM)
//   • 8 PM Sat CST → 02:00 UTC Sun → 07:30 IST Sun (7:30 AM)
function getISTLabel() {
  try {
    const tzPart = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Chicago",
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    const isCDT = tzPart && tzPart.value === "CDT";
    return isCDT ? "Sunday 6:30 AM IST" : "Sunday 7:30 AM IST";
  } catch (e) {
    // Safe fallback if Intl tz support is missing.
    return "Sunday 7:30 AM IST";
  }
}

const SiteHeader = () => {
  const { pathname } = useLocation();
  if (shouldSuppress(pathname)) return null;
  const themeId = pickThemeId(pathname);
  const t = THEMES[themeId];
  const istLabel = getISTLabel();

  return (
    <header className="relative z-30 w-full bg-kk-ink">
      <Link
        to="/"
        aria-label="Kathakala — The Art of Storytelling"
        className="block relative w-full overflow-hidden"
        style={{
          backgroundImage: `url(${t.backdrop})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Three-column grid: side wings stretch to match centre content
            height, so the bottom corner logos always align with the bottom
            of the centre text block. Wing widths are pinned so left and
            right padding around the centre is symmetric regardless of how
            wide each individual logo renders. */}
        <div className="grid grid-cols-[clamp(150px,18%,250px)_1fr_clamp(150px,18%,250px)] items-stretch gap-3 md:gap-6 lg:gap-10 px-3 md:px-5 lg:px-7 py-4 md:py-5">
          {/* ── Left wing: Kathakala wordmark (top) + B+ medallion (bottom) ── */}
          <div className="flex flex-col items-center justify-between gap-3">
            <img
              src="/logos/kathakala_logo.png"
              alt=""
              aria-hidden="true"
              className={`w-full max-h-24 md:max-h-32 lg:max-h-36 object-contain select-none ${t.logoShadow}`}
              loading="eager"
            />
            <img
              src="/logos/bplus_circular.png"
              alt=""
              aria-hidden="true"
              className={`h-20 w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 select-none ${t.logoGlowShadow}`}
              loading="eager"
            />
          </div>

          {/* ── Centre column: HERO++ Telugu / HERO+ English / HERO LIVE / Social ── */}
          <div className="flex flex-col items-center justify-center text-center px-2">
            {/* HERO++ — Telugu (single flowing line, dot-separated couplet) */}
          <p
            className={`font-krushna text-2xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight whitespace-nowrap ${t.teluguHero}`}
          >
            కథ చెప్పడం ఒక కళ
            <span className="opacity-60 mx-2 md:mx-3 lg:mx-4">·</span>
            కథ వినడం ఒక శోభ
          </p>
          {/* HERO+ — English */}
          <p
            className={`mt-2 md:mt-3 text-base md:text-xl lg:text-2xl xl:text-3xl italic font-serif ${t.english}`}
          >
            The Art of Storytelling
            <span className="opacity-60 mx-2 md:mx-3">·</span>
            The Joy of StoryListening
          </p>
          {/* HERO — LIVE pill with auto-IST (mixed case for readability;
              "LIVE" stays caps as a status indicator) */}
          <span
            className={`mt-3 md:mt-5 inline-flex items-center gap-2 md:gap-3 px-4 py-1.5 md:px-6 md:py-2.5 rounded-full text-sm md:text-base lg:text-lg font-medium ${t.livePill}`}
          >
            <span className="inline-block w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="font-bold tracking-wider">LIVE</span>
            <span>on YouTube.com/BPlusWithBhaskar — Every Saturday 8 PM CST</span>
            <span className="opacity-85 italic font-normal">
              (India: {istLabel})
            </span>
          </span>

          {/* Social row — subscribe / follow */}
          <div
            className={`mt-2.5 md:mt-3.5 flex items-center gap-2 md:gap-3 text-xs md:text-sm ${t.english}`}
          >
            <span className="opacity-90 italic">Subscribe / follow us</span>
            <a
              href="https://www.youtube.com/@BPlusWithBhaskar"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube · @BPlusWithBhaskar"
              onClick={(e) => e.stopPropagation()}
              className="hover:scale-110 hover:opacity-100 opacity-85 transition-all"
            >
              <FaYoutube className="text-base md:text-lg" />
            </a>
            <a
              href="https://www.facebook.com/BPlusWithBhaskar"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook · @BPlusWithBhaskar"
              onClick={(e) => e.stopPropagation()}
              className="hover:scale-110 hover:opacity-100 opacity-85 transition-all"
            >
              <FaFacebookF className="text-sm md:text-base" />
            </a>
            <a
              href="https://www.instagram.com/BPlusWithBhaskar"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram · @BPlusWithBhaskar"
              onClick={(e) => e.stopPropagation()}
              className="hover:scale-110 hover:opacity-100 opacity-85 transition-all"
            >
              <FaInstagram className="text-base md:text-lg" />
            </a>
            <a
              href="https://x.com/BPlusWithBhaskar"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X · @BPlusWithBhaskar"
              onClick={(e) => e.stopPropagation()}
              className="hover:scale-110 hover:opacity-100 opacity-85 transition-all"
            >
              <FaXTwitter className="text-sm md:text-base" />
            </a>
            <span className="opacity-90 font-medium ml-1">
              @BPlusWithBhaskar
            </span>
          </div>
          </div>

          {/* ── Right wing: BPlusWithBhaskar wordmark (top) + Kathakala emblem (bottom) ── */}
          <div className="flex flex-col items-center justify-between gap-3">
            <img
              src="/logos/bplus_with_bhaskar_logo.png"
              alt=""
              aria-hidden="true"
              className={`w-full max-h-20 md:max-h-24 lg:max-h-28 object-contain select-none ${t.logoShadow}`}
              loading="eager"
            />
            <img
              src="/logos/kathakala_emblem_circular.png"
              alt=""
              aria-hidden="true"
              className={`h-20 w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 select-none ${t.logoGlowShadow}`}
              loading="eager"
            />
          </div>
        </div>
      </Link>

      {/* Hairline gold rule with a soft glow. */}
      <div
        className={`h-[2px] w-full bg-gradient-to-r from-transparent ${t.rule} to-transparent shadow-[0_0_22px_rgba(212,168,67,0.3)]`}
      />

      {/* ── Theme wash tail — short bleed of the backdrop into the page so
          the transition from header to content isn't a hard cut. */}
      <div
        className="relative w-full h-12 md:h-16 lg:h-20 -mb-px"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(26,10,14,0.45) 0%, rgba(26,10,14,0.85) 60%, rgba(26,10,14,1) 100%), url(${t.backdrop})`,
          backgroundSize: "cover",
          backgroundPosition: "center 75%",
          backgroundRepeat: "no-repeat",
        }}
      />
    </header>
  );
};

export default SiteHeader;
